import { cn } from '@/lib/utils';
import { formatEur, type CashFlowEntry } from '@/lib/cashFlow';
import { relatedDocCells } from '@/lib/relatedDocuments';

/**
 * "Seotud dokumendid" — the prepayment-offset linkage panel (ETT ettemaksuarve ↔
 * ARV lõpparve) shown on an entry in EVERY lens off ONE model (`relatedDocCells`),
 * so a linked document is presented identically in the enriched detail and the
 * calendar/register hover (L14). The euro shown per row is the link's offset
 * capacity — a stored fact; the panel NEVER nets or computes an amount (finance-core
 * owns the resolved offset that actually corrects the status / projection; L9).
 * `onUnlink` and `onOpenRelated` are offered only where an action makes sense (the
 * detail); the read-only hover renders the same rows by omitting both.
 */
export function RelatedDocuments({
  entry,
  onUnlink,
  onOpenRelated,
  className,
}: {
  entry: CashFlowEntry;
  onUnlink?: (linkId: string) => void;
  onOpenRelated?: (entryId: string) => void;
  className?: string;
}) {
  const cells = relatedDocCells(entry);
  if (cells.length === 0) return null;

  return (
    <section className={cn('rounded-lg border border-border bg-card/40', className)}>
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Seotud dokumendid</h3>
        <span className="ml-auto text-[10px] text-muted-foreground/70">ettemaks ↔ lõpparve</span>
      </header>
      <ul className="flex flex-col gap-1 px-3 py-3">
        {cells.map((c) => {
          const title = c.docNumber ?? c.label ?? '—';
          return (
            <li
              key={c.linkId}
              className="flex items-center gap-2 rounded border border-transparent px-2 py-1 text-[11px] hover:border-border"
            >
              {c.code && (
                <span className={cn('shrink-0 rounded px-1 text-[9px] font-semibold tracking-wide', c.badgeCls)}>
                  {c.code}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  {onOpenRelated ? (
                    <button
                      type="button"
                      onClick={() => onOpenRelated(c.relatedEntryId)}
                      className="truncate font-mono text-foreground hover:text-[hsl(var(--primary))]"
                    >
                      {title}
                    </button>
                  ) : (
                    <span className="truncate font-mono text-foreground">{title}</span>
                  )}
                  <span className="truncate text-muted-foreground">{c.party}</span>
                </span>
                <span className="block text-[9px] uppercase tracking-wide text-muted-foreground/70">{c.caption}</span>
              </span>
              <span className="shrink-0 text-right tabular-nums text-foreground/85" title="Seotud summa">
                {formatEur(c.amountCents)}
              </span>
              {onUnlink && (
                <button
                  type="button"
                  aria-label="Eemalda seos"
                  onClick={() => onUnlink(c.linkId)}
                  className="shrink-0 rounded border border-border px-1 text-muted-foreground transition-colors hover:border-[hsl(var(--cost))]/50 hover:text-[hsl(var(--cost))]"
                >
                  <span aria-hidden>×</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
